import React, { useEffect, useState } from "react";
import PanelContainer from "../common/components/panel-container/panel";
import "./iso-file-management.scss";
import {
  BodyText,
  PageSubHeader,
} from "../common/components/title-container/LText";
import {
  Button,
  TrashIcon,
  ImportIcon,
  Checkbox,
  ShareIcon,
  CloseIcon,
  ProgressIndicator,
  RefreshIcon,
} from "@forge/common";
import DividerComponent from "../common/components/divider/divider-component";
import TableContainer from "../common/components/table-container/tableContainer";
import {
  FileManagement,
  ICustomizeViewColumns,
} from "./models/isoFileManagment.model";
import { useDispatch, useSelector } from "react-redux";
import { hideModel, showLoader, showModel } from "../store/model/model.action";
import { deleteMultipleISOPopup, deletePopup, shortenFilename } from "./popup";
import UploadIso from "./popup/iso-popup";
import { ISO_FILE_MANAGEMENT_CONSTANTS } from "./utils/constant";
import {
  cancelUpload,
  checkAllCheckbox,
  checkSingleCheckbox,
  deleteMultipleList,
  downloadIsoFile,
  fetchFileList,
  localSearch,
  searchText,
  sortingOrder,
  uploadFile,
  uploadLoader,
} from "./store/iso-file-management.action";
import { downloadRecordFile } from "../../../store/action";
import {
  dateFormatFun,
  rendererFileSizeCol,
} from "../file-management/utils/helper";
import { btnResponsive } from "../common/utils/helper";
import { config } from "../../../../common/authentication/config";
import useAuth from "../../../../common/hooks/useAuth";
import { PermissionConstant } from "../common/utils/permission";
import ButtonComponent from "../common/components/button/button";

const IsoFileManagement = () => {
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const { isoFileList, uploadMediaId, searchValue, uploadProgress, pageDetails } = useSelector(
    (state: any) => state?.isoFileManagementReducer
  );

  useEffect(() => {
    dispatch(fetchFileList(pageDetails?.pageNumber));
  }, []);

  const SubTitleContent = () => {
    return (
      <div className="flex justify-between gap-rem">
        <div className="flex items-center gap-rem">
          <PageSubHeader
            title={ISO_FILE_MANAGEMENT_CONSTANTS.LIST_OF_ISOS}
            subheading={`${isoFileList?.length || 0} total`}
          />
        </div>
        <div>
          {hasPermission(PermissionConstant.ISO_FILE_MANAGEMENT_UPLOAD) && (
            <ButtonComponent
              className={`${btnResponsive()} xl-high:!pr-[4rem]`}
              onClick={uploadIsonFile}
              data-testid={`upload_ISO_button`}
              size="regular"
              variant="primary"
              icon={<ShareIcon className={`${btnResponsive("text-base")}`} />}
              iconPosition="right"
              disabled={
                uploadProgress?.percentage > 0
                  ? true
                  : isoFileList?.filter((i: any) => i.checked)?.length > 0
              }
              title={ISO_FILE_MANAGEMENT_CONSTANTS.UPLOAD_ISO_FILES}
              tooltip={`Upload file for all VM's to Access`}
            />
          )}
        </div>
      </div>
    );
  };

  const showDeletePopup = (data: FileManagement, key: number) => {
    dispatch(
      showModel({
        childTemplate: () =>
          deletePopup({
            btnText: ISO_FILE_MANAGEMENT_CONSTANTS.CANCEL,
            btnText1: ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_ANYWAY,
            title: ISO_FILE_MANAGEMENT_CONSTANTS.FILE_DELETE_ISO_FILE,
            description: `${
              ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_DESCRIPTION
            } “${shortenFilename(data.name)}” ${
              ISO_FILE_MANAGEMENT_CONSTANTS.ISO_FILE
            }?`,
            onCancel: () => dispatch(hideModel()),
            onTrigger: () => {
              dispatch(deleteMultipleList([data?.mediaId]));
              dispatch(hideModel());
            },
          }),
      })
    );
  };

  const addLeading = () => {
    return `break-all overflow-wrap-anywhere xl-high:!leading-[50px] xl-mid:!leading-[25px] `;
  };

  const handlePermssionforAction = () => {
    if (
      hasPermission(PermissionConstant.ISO_FILE_MANAGEMENT_DOWNLOAD) ||
      hasPermission(PermissionConstant.ISO_FILE_MANAGEMENT_DELETE)
    ) {
      return {
        key: "status",
        name: "Actions",
        className: "!w-[180px]",
        render: (data: any) => {
          return (
            <div className="flex align-center gap-3 cursor-pointer rounded-xs">
              <div className="hover:bg-[#103F61]">
                {hasPermission(
                  PermissionConstant.ISO_FILE_MANAGEMENT_DOWNLOAD
                ) && (
                  <ImportIcon
                    title="download"
                    className={`cursor-pointer ${btnResponsive("icons")} p-0.5`}
                    onClick={() => {
                      dispatch(showLoader());
                      dispatch(downloadIsoFile(data));
                    }}
                  />
                )}
              </div>
              <div className="hover:bg-[#103F61] rounded-xs">
                {hasPermission(
                  PermissionConstant.ISO_FILE_MANAGEMENT_DELETE
                ) && (
                  <TrashIcon
                    title="delete"
                    data-testid={`iso-trash-icon`}
                    className={`cursor-pointer ${btnResponsive("icons")} p-0.5`}
                    onClick={() => showDeletePopup(data, 0)}
                  />
                )}
              </div>
            </div>
          );
        },
      };
    }
    return undefined;
  };

  const checkHandlePermssion = () => {
    if (hasPermission(PermissionConstant.ISO_FILE_MANAGEMENT_DELETE)) {
      return {
       key: "checkbox",
      name: "checkbox",
        checkbox: true,
        className: "!w-[10px]",
        checked:
          isoFileList?.length > 0
            ? isoFileList?.every((item: FileManagement) => !!item.checked)
            : false,
        // conform to ICustomizeViewColumns: onChange(key: string, checked: boolean)
        onChange: (e: { target: { checked: boolean } }) => {
          dispatch(checkAllCheckbox(e.target.checked));
        },
        render: (data: FileManagement) => {
          const index = isoFileList?.findIndex(
            (item: FileManagement) => item.mediaId === data.mediaId
          );
          const rowIndex = index !== undefined && index > -1 ? index : 0;
          return (
            <Checkbox
              className="cursor-pointer"
              id={`overall_check_${rowIndex}`}
              data-testid={`overall_check`}
              checked={data?.checked}
              onChange={(e) => {
                dispatch(checkSingleCheckbox(rowIndex, e.target.checked));
              }}
            />
          );
        },
      };
    }
  };

  const baseColumns = [
    {
      key: "name",
      name: "File Name",
      className: "!w-[260px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "name"));
      },
      render: (data) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              tooltip={data?.name}
              title={data?.name}
              className={`${addLeading()}`}
            />
          </div>
        );
      },
    },
    {
      key: "storageB",
      name: "size",
      className: "!w-[120px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "size"));
      },
      render: (data) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              tooltip={rendererFileSizeCol(data?.storageB)}
              title={rendererFileSizeCol(data?.storageB)}
            />
          </div>
        );
      },
    },
    {
      key: "creationDate",
      name: "Date Updated",
      className: "!w-[180px]",
      onSort: (direction: "asc" | "desc") => {
        dispatch(sortingOrder(direction, "creationDate"));
      },
      render: (data: any) => {
        return (
          <div className="flex w-full overflow-hidden cursor-pointer">
            <BodyText
              tooltip={dateFormatFun(data.creationDate)}
              title={dateFormatFun(data.creationDate)}
              className={`${addLeading()}`}
            />
          </div>
        );
      },
    },
  ] as ICustomizeViewColumns[];

  
const checkboxPermission = checkHandlePermssion();
const actionColumn = handlePermssionforAction();

const column: ICustomizeViewColumns[] = [
  ...(checkboxPermission ? [checkboxPermission] : []),
  ...baseColumns,
  ...(actionColumn ? [actionColumn] : []),
];


  const deleteList = () => {
    const selectedItems = isoFileList
      .filter((item: FileManagement) => item.checked === true)
      .map((item: FileManagement) => item.mediaId);
    dispatch(
      showModel({
        childTemplate: () =>
          deleteMultipleISOPopup({
            btnText: ISO_FILE_MANAGEMENT_CONSTANTS.CANCEL,
            btnText1: ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_ANYWAY,
            title: ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_MULTIPLE_ISOS,
            description: `${ISO_FILE_MANAGEMENT_CONSTANTS.FILE_CANNOT_DELETE_DESCRIPTION}`,
            selectedItems: isoFileList.filter(
              (item: FileManagement) => item.checked
            ),
            onCancel: () => dispatch(hideModel()),
            onTrigger: () => {
              dispatch(deleteMultipleList(selectedItems));
              dispatch(hideModel());
            },
          }),
      })
    );
  };

  const uploadIsonFile = () => {
    dispatch(
      showModel({
        childTemplate: () => (
          <UploadIso
            title={ISO_FILE_MANAGEMENT_CONSTANTS.UPLOAD_ISO_FILE}
            btnText={ISO_FILE_MANAGEMENT_CONSTANTS.CANCEL}
            btnText1={ISO_FILE_MANAGEMENT_CONSTANTS.UPLOAD}
            onCancel={() => dispatch(hideModel())}
            onTrigger={(files: File[]) => {
              dispatch(showLoader());
              let payload = {
                name: files[0]?.name || "",
                catalog: {
                  id: `unv:could:cloud:${config?.ACADEMY_TENANT_ID}`,
                },
                size: files[0]?.size?.toString() || "",
                description: files[0]?.name || "",
                imageType: "iso",
                file: files[0],
              };
              dispatch(uploadFile(payload));
              dispatch(hideModel());
            }}
          />
        ),
      })
    );
  };

  const filterSearchData = (value: string) => {
    dispatch(localSearch(value));
  };

  const slotRight = () => {
    return (
      <div className="progress-bar-iso-component">
      {console.log(uploadProgress?.percentage)}
        {uploadProgress?.percentage > 0 && (
          <div className="flex justify-between gap-2 xl-high:mt-2">
            <div>
              <BodyText
                title={`File Upload Progress:`}
                className={`xl-high:leading-[29px]`}
              />
              <BodyText
                title={`${shortenFilename(uploadProgress?.name, 30)}`}
                className={`xl-high:mt-1 xl-high:leading-[29px]`}
              />
            </div>
            <div>
              <div className="flex justify-between gap-2">
                <BodyText
                  title={`${uploadProgress?.uploadMB} of ${
                    uploadProgress?.totalMB
                  } (${Math.trunc(uploadProgress?.percentage)})%`}
                />
                {uploadMediaId && (
                <CloseIcon
                  data-test-id="close-icon"
                  className="cursor-pointer"
                  onClick={() => {
                    dispatch(cancelUpload(uploadProgress?.jobId));
                    // dispatch(uploadLoader("0", "0", "0", ""));
                    // window.location.reload();
                  }}
                />
              )}
              </div>
              <ProgressIndicator
                id="iso-progress-bar"
                data-testid="iso-progress-bar"
                fileName={"uploadProgress?.fileName"}
                fileSize={100}
                size="regular"
                uploaded={uploadProgress?.percentage}
                className={`w-[320px] xl-high:w-[420px] xl-high:mt-2`}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const tableRightSlot = () => {
    return (
      <div className="flex justify-end items-center gap-2">
        {renderSlotRight()}
        {hasPermission(PermissionConstant.ISO_FILE_MANAGEMENT_DELETE) && (
          <ButtonComponent
            id="iso-delete"
            data-testid="iso-delete"
            className={`xl-high:!pr-[4rem]`}
            onClick={() => {

              if (isoFileList?.filter((i: any) => i.checked)?.length === 1) {
                showDeletePopup(isoFileList?.filter((i: any) => i.checked)[0], 0);
                
              }
              else{
                deleteList();
              }

            }}
            size="regular"
            variant="primary"
            disabled={isoFileList?.filter((i: any) => i.checked)?.length === 0}
            tooltip={"Delete the selected items across VMs"}
            title={ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_SELECTED}
          />
        )}
      </div>
    );
  };

  const renderSlotRight = () => {
    const handleRefreshClick = () => {
      dispatch(showLoader());
      dispatch(fetchFileList(1, true));
    };

    return (
      <div
        id="iso-refresh"
        data-testid="iso-refresh"
        className="cursor-pointer mr-4"
        onClick={handleRefreshClick}
      >
        <RefreshIcon className={btnResponsive("icons")} title="Refresh" />
      </div>
    );
  };

  return (
    <PanelContainer
      title={ISO_FILE_MANAGEMENT_CONSTANTS.WELCOME_TO_ISO_FILE_MANAGEMENT}
      headerRight={slotRight()}
      data-testid="iso-panel-container"
    >
      <SubTitleContent />
      <div className="mt-1 mb-2">
        <DividerComponent />
      </div>
      <TableContainer
        hidePageCount
        column={column}
        data={isoFileList}
        btnText={ISO_FILE_MANAGEMENT_CONSTANTS.DELETE_SELECTED}
        totalRecords={isoFileList?.length ?? 0}
        btnClassName="table-delete-button"
        slotRight={tableRightSlot()}
        onSearchFunction={(value: string) => {
          filterSearchData(value);
          dispatch(searchText(value));
        }}
        searchValue={searchValue}
      />
    </PanelContainer>
  );
};

export default IsoFileManagement;
